// src/pages/kuyumcu/KuyumcuCantaPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";
import { usePermission } from "../../context/PermissionContext";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";


import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetDataGrid from "./components/KuyumcuDataGrid";
import ProsetPopup from "./components/ProsetPopup";
import SilmeUyariPopup from "./components/SilmeUyariPopup";

import { formatNumberTR } from "./utils/numberFormat";

import {
  Box,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Chip,
} from "@mui/material";
import { Edit, Delete, Calculate } from "@mui/icons-material";

import {
  FaShoppingBag,
} from "react-icons/fa";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuCantaPage.css";

/** datetime-local (yyyy-MM-ddTHH:mm) için güvenli format */
const toInputDateTime = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** Son 1 ay (aynı gün): bugün 17 ise geçen ay 17 */
const getLastMonthSameDayRange = () => {
  const now = new Date();

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const prev = new Date(todayStart);
  const currentDay = prev.getDate();
  const targetMonth = prev.getMonth() - 1;

  prev.setMonth(targetMonth);

  // Ay kısaldıysa (31 -> 2-3 gün kayma), ayın son gününe çek
  if (prev.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    prev.setDate(0);
  } else {
    prev.setDate(Math.min(currentDay, prev.getDate()));
  }

  const fmt = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  return { start: fmt(prev), end: fmt(todayStart) };
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function KuyumcuCantaPage() {
  const navigate = useNavigate();
  const userId = getUserId();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const { can, isLoading } = usePermission();
  // ====== ÇANTA YETKİ KODLARI ======
  const PERM_CANTA_VIEW = "CANTA_VIEW";       // 101
  const PERM_CANTA_CREATE = "CANTA_CREATE";   // 102
  const PERM_CANTA_UPDATE = "CANTA_UPDATE";   // 103
  const PERM_CANTA_DELETE = "CANTA_DELETE";   // 104

  const canView = !isLoading && can(PERM_CANTA_VIEW);
  const canCreate = !isLoading && can(PERM_CANTA_CREATE);
  const canUpdate = !isLoading && can(PERM_CANTA_UPDATE);
  const canDelete = !isLoading && can(PERM_CANTA_DELETE);

  /* =========================
     Login kontrol
  ========================= */
  useEffect(() => {
    if (!userId || !token) navigate("/login");
  }, [userId, token, navigate]);

  /* =========================
     Filtre: varsayılan SON 1 AY (aynı gün)
  ========================= */
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");

  /* =========================
     Bölge Kod listesi
  ========================= */
  const [bolgeKodList, setBolgeKodList] = useState([]);

  const fetchBolgeKodList = async () => {
    try {
      if (!userId) return;
      const resp = await axiosInstance.get(`/parametreler/bolge-kod?userId=${userId}`);
      setBolgeKodList(resp.data || []);
    } catch (e) {
      setBolgeKodList([]);
    }
  };

  /* =========================
     Grid Data
  ========================= */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // “Hesapla” sonrası arka arkaya çağrılarda yarış durumu olmasın diye:
  const lastLoadReqIdRef = useRef(0);

  const loadCanta = async () => {
    if (isLoading) return rows;

    if (!can(PERM_CANTA_VIEW)) {
      setRows([]);          // yetkisi yoksa listeyi boşalt
      setLoading(false);
      return [];
    }

    if (!userId) return rows;

    const reqId = ++lastLoadReqIdRef.current;
    setLoading(true);

    try {
      const params = { userId };

      // sadece ikisi de doluysa filtre uygula
      if (filterStart && filterEnd) {
        params.start = filterStart;
        params.end = filterEnd;
      }

      const resp = await axiosInstance.get("/canta", { params });


      // Daha yeni bir istek varsa, bunu ezme
      if (reqId !== lastLoadReqIdRef.current) return rows;

      const data = resp.data || [];
      setRows(data);
      return data;
    } catch (e) {
      console.error("Çanta kayıtları alınamadı:", e);

      // Kritik: hata anında tabloyu boşaltma -> “datagrid kayboluyor” hissi biter
      // setRows([]);  <-- KALDIRILDI

      return rows; // ekrandaki veriyi koru
    } finally {
      // Daha yeni bir istek başladıysa loading’i onun bitirmesine izin ver
      if (reqId === lastLoadReqIdRef.current) setLoading(false);
    }
  };

  const safeReloadCantaAfterCalc = async () => {
    // Hesapla sonrası DB commit gecikmesi olabiliyor; 3 deneme yap
    for (let i = 0; i < 3; i++) {
      const list = await loadCanta();
      // Liste boş olsa bile “en azından tamamen yok etme” için rows koruması var.
      // Ama yine de 0 geliyorsa kısa bekleyip yeniden dene:
      if (Array.isArray(list) && list.length > 0) return list;
      await sleep(350);
    }
    return rows;
  };

  useEffect(() => {
    fetchBolgeKodList();
    // eslint-disable-next-line
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    loadCanta();
    // eslint-disable-next-line
  }, [userId, filterStart, filterEnd, isLoading]);

  /* =========================
     Popup State (Ekle / Düzenle)
  ========================= */
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const blankForm = {
    id: null,
    baslangicTarihi: "",
    bitisTarihi: "",
    bolgeKodId: "",
    cikisTeraziGram: "",
    cikisHasGram: "",
    // backend hesapla ile doldurulan alanlar:
    alinanHasGram: 0,
    verilenHasGram: 0,
    kalanHasGram: 0,
    alinanIscilikGram: 0,
    verilenIscilikGram: 0,
  };

  const [form, setForm] = useState(blankForm);

  const openCreate = () => {
    setEditMode(false);
    setForm(blankForm);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setForm({
      ...blankForm,
      id: row.id,
      baslangicTarihi: toInputDateTime(row.baslangicTarihi),
      bitisTarihi: toInputDateTime(row.bitisTarihi),
      bolgeKodId: row.bolgeKodId ?? "",
      cikisTeraziGram: row.cikisTeraziGram ?? "",
      cikisHasGram: row.cikisHasGram ?? "",
      alinanHasGram: row.alinanHasGram ?? 0,
      verilenHasGram: row.verilenHasGram ?? 0,
      kalanHasGram: row.kalanHasGram ?? 0,
      alinanIscilikGram: row.alinanIscilikGram ?? 0,
      verilenIscilikGram: row.verilenIscilikGram ?? 0,
    });
    setOpen(true);
  };

  /* =========================
     Silme Popup
  ========================= */
  const [silmePopupOpen, setSilmePopupOpen] = useState(false);
  const [silinecekRow, setSilinecekRow] = useState(null);

  const askDelete = (row) => {
    setSilinecekRow({ id: row?.id });
    setSilmePopupOpen(true);
  };

  const doDelete = async () => {
    if (!silinecekRow?.id) return;

    if (isLoading) return;

    if (!can(PERM_CANTA_DELETE)) {
      alert("Silme yetkiniz yok.");
      return;
    }

    try {
        await axiosInstance.delete(`/canta/${silinecekRow.id}`, { params: { userId } });

        // ✅ Eğer düzenleme popup açıksa ve silinen kayıt o kayıtsa popup kapansın
        if (open && editMode && form?.id === silinecekRow.id) {
          setOpen(false);
          setEditMode(false);
          setForm(blankForm);
        }

        setSilmePopupOpen(false);
        setSilinecekRow(null);

        // silme sonrası yenile
        await loadCanta();
    } catch (e) {
      console.error("Çanta silinemedi:", e);
      alert("Silme sırasında hata oluştu.");
    }
  };

  /* =========================
     Kaydet (Ekle / Güncelle)
  ========================= */
  const handleSave = async () => {

    if (isLoading) return;

    const needPerm = editMode ? PERM_CANTA_UPDATE : PERM_CANTA_CREATE;
    if (!can(needPerm)) {
      alert("Bu işlem için yetkiniz yok.");
      return;
    }

    try {
      if (!userId) return;

      const payload = {
        kullaniciId: userId,
        baslangicTarihi: form.baslangicTarihi ? new Date(form.baslangicTarihi).toISOString() : null,
        bitisTarihi: form.bitisTarihi ? new Date(form.bitisTarihi).toISOString() : null,
        bolgeKodId: form.bolgeKodId ? Number(form.bolgeKodId) : null,
        cikisTeraziGram: form.cikisTeraziGram === "" ? null : Number(form.cikisTeraziGram),
        cikisHasGram: form.cikisHasGram === "" ? null : Number(form.cikisHasGram),
      };

      if (!editMode) {
        await axiosInstance.post("/canta", payload, { params: { userId } });
      } else {
        await axiosInstance.put(`/canta/${form.id}`, payload, { params: { userId } });
      }

      setOpen(false);
      setEditMode(false);
      setForm(blankForm);

      await loadCanta();
    } catch (e) {
      console.error("Çanta kaydedilemedi:", e);
      alert("Kayıt sırasında hata oluştu.");
    }
  };

  /* =========================
     Hesapla (Grid butonu + Popup içi)
  ========================= */
  const [calcLoadingId, setCalcLoadingId] = useState(null);

  const handleHesapla = async (id) => {
    if (!id) return;

    if (isLoading) return;

    if (!can(PERM_CANTA_UPDATE)) {
      alert("Hesapla işlemi için yetkiniz yok.");
      return;
    }

    setCalcLoadingId(id);
    try {
      await axiosInstance.post(`/canta/${id}/hesapla`, null, { params: { userId } });

      const latestList = await safeReloadCantaAfterCalc();

      // Edit popup açıksa formu güncelle
      if (open && editMode && form.id === id) {
        const latest = (latestList || []).find((r) => r.id === id);
        if (latest) {
          setForm((p) => ({
            ...p,
            alinanHasGram: latest.alinanHasGram ?? p.alinanHasGram,
            verilenHasGram: latest.verilenHasGram ?? p.verilenHasGram,
            kalanHasGram: latest.kalanHasGram ?? p.kalanHasGram,
            alinanIscilikGram: latest.alinanIscilikGram ?? p.alinanIscilikGram,
            verilenIscilikGram: latest.verilenIscilikGram ?? p.verilenIscilikGram,
          }));
        }
      }
    } catch (e) {
      console.error("Hesaplama başarısız:", e);
      alert("Hesaplama sırasında hata oluştu.");
    } finally {
      setCalcLoadingId(null);
    }
  };

  /* =========================
     Grid Kolonlar
  ========================= */
  const columns = useMemo(() => {
    const fmt = (v) => formatNumberTR(v, 2);

    return [
      {
        field: "hesapla",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Button
            size="small"
            variant="contained"
            className="canta-hesapla-btn"
            startIcon={<Calculate />}
            disabled={calcLoadingId === p.row.id}
            onClick={(e) => {
              e.stopPropagation();
              handleHesapla(p.row.id);
            }}
          >
            {calcLoadingId === p.row.id ? "..." : "Hesapla"}
          </Button>
        ),
      },

      {
        field: "baslangicTarihi",
        headerName: "Başlangıç Tarihi",
        minWidth: 170,
        flex: 1,
        renderCell: (p) =>
          p.row.baslangicTarihi
            ? new Date(p.row.baslangicTarihi).toLocaleString("tr-TR")
            : "-",
      },
      {
        field: "bitisTarihi",
        headerName: "Bitiş Tarihi",
        minWidth: 170,
        flex: 1,
        renderCell: (p) =>
          p.row.bitisTarihi
            ? new Date(p.row.bitisTarihi).toLocaleString("tr-TR")
            : "-",
      },

      {
        field: "bolgeKod",
        headerName: "Bölge Kod",
        minWidth: 120,
        flex: 1,
        renderCell: (p) => p.row.bolgeKod || "-",
      },

      {
        field: "cikisTeraziGram",
        headerName: "Çıkış Terazi",
        minWidth: 120,
        flex: 1,
        valueGetter: (p) => p.row?.cikisTeraziGram,
        renderCell: (p) => fmt(p.row?.cikisTeraziGram),
      },
      {
        field: "cikisHasGram",
        headerName: "Çıkış Has",
        minWidth: 120,
        flex: 1,
        valueGetter: (p) => p.row?.cikisHasGram,
        renderCell: (p) => fmt(p.row?.cikisHasGram),
      },

      {
        field: "alinanHasGram",
        headerName: "Alınan Has",
        minWidth: 120,
        flex: 1,
        renderCell: (p) => fmt(p.row?.alinanHasGram),
        cellClassName: "canta-calc-cell",
      },
      {
        field: "verilenHasGram",
        headerName: "Verilen Has",
        minWidth: 120,
        flex: 1,
        renderCell: (p) => fmt(p.row?.verilenHasGram),
        cellClassName: "canta-calc-cell",
      },
      {
        field: "kalanHasGram",
        headerName: "Kalan Has",
        minWidth: 120,
        flex: 1,
        renderCell: (p) => fmt(p.row?.kalanHasGram),
        cellClassName: "canta-calc-strong",
      },

      {
        field: "alinanIscilikGram",
        headerName: "Alınan İşçilik",
        minWidth: 130,
        flex: 1,
        renderCell: (p) => fmt(p.row?.alinanIscilikGram),
        cellClassName: "canta-calc-cell",
      },
      {
        field: "verilenIscilikGram",
        headerName: "Verilen İşçilik",
        minWidth: 130,
        flex: 1,
        renderCell: (p) => fmt(p.row?.verilenIscilikGram),
        cellClassName: "canta-calc-cell",
      },

      {
        field: "actions",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <div style={{ display: "flex", gap: 8 }}>
            {canUpdate && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(p.row);
                }}
                sx={{
                  backgroundColor: "rgba(184,134,11,0.10)",
                  border: "1px solid rgba(184,134,11,0.22)",
                  "&:hover": { backgroundColor: "rgba(184,134,11,0.16)" },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}

            {canDelete && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  askDelete(p.row);
                }}
                sx={{
                  backgroundColor: "rgba(198,40,40,0.10)",
                  border: "1px solid rgba(198,40,40,0.25)",
                  "&:hover": { backgroundColor: "rgba(198,40,40,0.18)" },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}

          </div>
        ),
      },
    ];
    // eslint-disable-next-line
  }, [calcLoadingId, open, editMode, form]);

  /* =========================
     UI
  ========================= */
  return (
    <div className="main-container canta-page">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="content">
        <h1 className="canta-title" style={{ color: "#b8860b", fontSize: "30px" }}>
         <FaShoppingBag /> Çanta
        </h1>

        {/* ÜST BAR: Filtre + Kayıt ekle */}
        <div className="canta-topbar">
          <div className="canta-filter">
            <TextField
              type="date"
              size="small"
              label="Başlangıç Tarihi"
              InputLabelProps={{ shrink: true }}
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
            />
            <TextField
              type="date"
              size="small"
              label="Bitiş Tarihi"
              InputLabelProps={{ shrink: true }}
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
            />

            <Chip
              className="canta-chip"
              label="Son 1 ay"
              onClick={() => {
                const r = getLastMonthSameDayRange();
                setFilterStart(r.start);
                setFilterEnd(r.end);
              }}
            />
          </div>

          {canCreate && (
            <Button startIcon={<AddCircleOutlineIcon />} variant="contained" className="canta-add-btn" onClick={openCreate}>
              Kayıt Ekle
            </Button>
          )}
        </div>

        {/* GRID */}
        <Box className="canta-grid-wrap">
          <ProsetDataGrid
            rows={rows || []}
            columns={columns}
            pageSize={7}
            noRowsLabel={loading ? "Yükleniyor..." : "Kayıt Bulunamadı"}
            onRowClick={(params) => {
              if (canUpdate) openEdit(params.row);
            }}
          />
        </Box>

        {/* POPUP (Ekle/Düzenle) */}
        <ProsetPopup
          open={open}
          title={editMode ? "Çanta Kaydı Düzenle" : "Çanta Kaydı Ekle"}
          editMode={editMode}
          onSave={handleSave}
          onCancel={() => {
            setOpen(false);
            setEditMode(false);
            setForm(blankForm);
          }}
          onDelete={
            editMode && canDelete
              ? () => {
                  if (!form?.id) return;
                  setSilinecekRow({ id: form.id });
                  setSilmePopupOpen(true);
                }
              : undefined
          }

        >
          <div className="canta-popup-grid">
            <TextField
              type="datetime-local"
              label="Başlangıç Tarihi"
              InputLabelProps={{ shrink: true }}
              value={form.baslangicTarihi}
              onChange={(e) =>
                setForm((p) => ({ ...p, baslangicTarihi: e.target.value }))
              }
            />

            <TextField
              type="datetime-local"
              label="Bitiş Tarihi"
              InputLabelProps={{ shrink: true }}
              value={form.bitisTarihi}
              onChange={(e) =>
                setForm((p) => ({ ...p, bitisTarihi: e.target.value }))
              }
            />

            <TextField
              select
              label="Bölge Kod"
              value={form.bolgeKodId}
              onChange={(e) =>
                setForm((p) => ({ ...p, bolgeKodId: e.target.value }))
              }
            >
              <MenuItem value="">Seçiniz</MenuItem>
              {(bolgeKodList || []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.bolgeKod}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Çıkış Terazi Gram"
              type="number"
              value={form.cikisTeraziGram}
              onChange={(e) =>
                setForm((p) => ({ ...p, cikisTeraziGram: e.target.value }))
              }
            />

            <TextField
              label="Çıkış Has Gram"
              type="number"
              value={form.cikisHasGram}
              onChange={(e) =>
                setForm((p) => ({ ...p, cikisHasGram: e.target.value }))
              }
            />

            {/* Hesaplananlar (disabled) */}
            <div className="canta-calc-section">
              <div className="canta-calc-title">Hesaplama Alanları</div>

              <TextField
                label="Alınan Has Gram"
                value={formatNumberTR(form.alinanHasGram, 2)}
                disabled
              />
              <TextField
                label="Verilen Has Gram"
                value={formatNumberTR(form.verilenHasGram, 2)}
                disabled
              />
              <TextField
                label="Kalan Has Gram"
                value={formatNumberTR(form.kalanHasGram, 2)}
                disabled
              />
              <TextField
                label="Alınan İşçilik"
                value={formatNumberTR(form.alinanIscilikGram, 2)}
                disabled
              />
              <TextField
                label="Verilen İşçilik"
                value={formatNumberTR(form.verilenIscilikGram, 2)}
                disabled
              />

              {/* Popup içinde de hesapla butonu isterseniz */}
              {editMode && form.id && (
                <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    className="canta-hesapla-btn"
                    startIcon={<Calculate />}
                    disabled={calcLoadingId === form.id}
                    onClick={() => handleHesapla(form.id)}
                  >
                    {calcLoadingId === form.id ? "Hesaplanıyor..." : "Hesapla"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </ProsetPopup>

        {/* SİLME POPUP */}
        <SilmeUyariPopup
          open={silmePopupOpen}
          onCancel={() => {
            setSilmePopupOpen(false);
            setSilinecekRow(null);
          }}
          onConfirm={doDelete}
          message="Bu çanta kaydını silmek istediğinize emin misiniz?"
        />
      </div>
    </div>
  );
}
